import { memo, useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DeliveryErrorTooltip } from '@/components/atoms/DeliveryErrorTooltip/DeliveryErrorTooltip';
import { cn } from '@/lib/utils';
import { AuthorType, Message as MessageType, ConversationSource } from '@/shared/types/chat';
import { IntegrationType } from '@/shared/types/integration';

import Message from '../Message';
import MessageStatus from '../MessageStatus/MessageStatus';

type Props = {
  message: MessageType;
  showTime: boolean;
  showAvatar: boolean;
  source: ConversationSource;
  channel?: IntegrationType;
};

export const MessageGroup = memo(({ message, showTime, showAvatar, source, channel }: Props) => {
  const { failed_at, author_type, error_code } = message;
  const isVisitor = author_type === AuthorType.VISITOR;
  const { t } = useTranslation();
  const [attachmentError, setAttachmentError] = useState<Error | null>(null);
  const handleAttachmentError = useCallback(
    (error?: Error) => {
      setAttachmentError(error ?? new Error(t('conversation.attachment_error')));
    },
    [t]
  );

  const failureLabel = (
    <span className="text-sm leading-5 font-normal text-destructive">
      {failed_at ? t('conversation.message_failed') : attachmentError?.message}
    </span>
  );

  const failed = Boolean(failed_at || attachmentError);

  return (
    <div className={cn('flex w-full flex-col gap-1', isVisitor ? 'items-start' : 'items-end')}>
      <Message
        message={message}
        showAvatar={showAvatar}
        group={showTime}
        source={source}
        handleAttachmentError={handleAttachmentError}
      />
      {failed ? (
        <div className={cn('flex items-center', isVisitor ? 'px-0.5' : 'px-5.5')}>
          <DeliveryErrorTooltip errorCode={failed_at ? error_code : undefined}>
            {failureLabel}
          </DeliveryErrorTooltip>
        </div>
      ) : (
        showTime && (
          <MessageStatus
            created={message.created}
            authorType={author_type}
            channel={channel}
            isVoiceNote={message.body?.type === 'audio' && Boolean(message.text)}
          />
        )
      )}
    </div>
  );
});

MessageGroup.displayName = 'MessageGroup';
